import type {Feature, FeatureConnection} from "../feature";
import type {GraphAtom} from "../graph/GraphAtom";
import type {MolecularGraph} from "../graph/MolecularGraph";
import {stableFeatureId} from "./stableFeatureId";
import {classifyFeature} from "./classifyFeature";
import {computeFusion} from "./computeFusion";
import {nameAtom} from "../naming/nameAtom";
import {nameFeature} from "../naming/nameFeature";
import {indexElementsInScope} from "../naming/elementIndexer";
import {computeMoleculeElementIndices} from "../naming/moleculeAtomIndexer";
import type {
    SerializedFeature,
    SerializedAtom,
    SerializedBond,
    SerializationResult,
    SerializedFeatureConnection,
} from "./SerializedFeature";

/**
 * Serialize a runtime feature graph into JSON-safe form with stable IDs.
 *
 * The runtime `Feature` type uses object references for parent/children/siblings
 * which can't be JSON.stringify'd safely. This function flattens those references
 * into stable string IDs and exposes a number of derived fields that consumers
 * (e.g. the alt-text generator) previously had to recompute.
 *
 * @param features The features produced by `molecularFeatureExtractor.extract`
 *                 (must have been processed by `featureIdentifier.applyIdentifiers`
 *                  so each feature has a `uniqueName`)
 * @param graph    The molecular graph the features were extracted from
 */
export function serializeFeatures(
    features: Feature[],
    graph: MolecularGraph,
): SerializationResult {
    // Phase 1: assign stable IDs to every feature up front so cross-references work
    const idByFeature = new Map<Feature, string>();
    for (const f of features) {
        idByFeature.set(f, stableFeatureId(f));
    }

    // Phase 2: detect ring fusion (item 6)
    const fusionMap = computeFusion(features);

    // Phase 3: collect "shared atoms" — atoms appearing in more than one top-level feature
    // (item 9). Mirrors the logic in featureIdentifier.getAtomNamesBasedOnFeature.
    const atomToFeatures = new Map<GraphAtom, Set<Feature>>();
    for (const f of features) {
        for (const atom of f.atoms) {
            let set = atomToFeatures.get(atom);
            if (!set) {
                set = new Set();
                atomToFeatures.set(atom, set);
            }
            set.add(f);
        }
    }

    // Phase 3.5: derive parent feature for each functional group (item 4).
    // The runtime Feature.parent is only set by the navigation extractor pipeline,
    // not by molecularFeatureExtractor. Derive it here so the serialization layer
    // is self-contained: a functional group's parent is the structural primary
    // feature (ring, chain, bridged_bicyclic, etc.) that contains the most of its
    // atoms — falling back to one that contains its connection atoms.
    const parentByFeature = new Map<Feature, Feature>();
    const primaryTypes = new Set(["ring", "chain", "bridged_bicyclic", "atom_cluster", "molecule"]);
    const primaryFeatures: Array<{feature: Feature; atomSet: Set<GraphAtom>}> = features
        .filter(f => primaryTypes.has(classifyFeature(f)))
        .map(f => ({feature: f, atomSet: new Set(f.atoms)}));
    // PIPH-231: atom-type features may be bonded to a functional group rather
    // than directly to a primary (e.g. TBDMS on the oxygen of an ether). When
    // that happens we want the FG to be the recorded parent so the renderer can
    // surface the full attachment path back to the primary.
    const fgFeatures: Array<{feature: Feature; atomSet: Set<GraphAtom>}> = features
        .filter(f => classifyFeature(f) === "functional_group")
        .map(f => ({feature: f, atomSet: new Set(f.atoms)}));

    for (const f of features) {
        const fType = classifyFeature(f);
        // PIPH-122: Also derive parents for atom-type features (standalone atoms/molecules
        // that may be attached to a primary feature, e.g., H₃O⁺ on a chain)
        if (fType !== "functional_group" && fType !== "atom") continue;

        // Try atom overlap first (functional group atoms inside a primary feature)
        let bestParent: Feature | undefined;
        let maxOverlap = 0;
        for (const {feature: pf, atomSet} of primaryFeatures) {
            const overlap = f.atoms.filter(a => atomSet.has(a)).length;
            if (overlap > maxOverlap) {
                maxOverlap = overlap;
                bestParent = pf;
            }
        }
        if (bestParent && maxOverlap > 0) {
            parentByFeature.set(f, bestParent);
            continue;
        }
        // Fall back to connection-atom overlap (functional group attached to but
        // not overlapping a primary, e.g. a halide on a ring)
        let primaryByConnection: Feature | undefined;
        for (const {feature: pf, atomSet} of primaryFeatures) {
            const connOverlap = f.connections.filter(a => atomSet.has(a)).length;
            if (connOverlap > 0) {
                primaryByConnection = pf;
                break;
            }
        }
        if (primaryByConnection) {
            parentByFeature.set(f, primaryByConnection);
            continue;
        }
        // PIPH-231: For atom-type features only, allow a functional group to be
        // the parent when no primary matches. Pick the FG with the largest
        // connection-atom overlap; ties broken by feature order.
        if (fType === "atom") {
            let bestFg: Feature | undefined;
            let bestFgOverlap = 0;
            for (const {feature: fg, atomSet} of fgFeatures) {
                const connOverlap = f.connections.filter(a => atomSet.has(a)).length;
                if (connOverlap > bestFgOverlap) {
                    bestFgOverlap = connOverlap;
                    bestFg = fg;
                }
            }
            if (bestFg) {
                parentByFeature.set(f, bestFg);
            }
        }
    }

    // PIPH-309 follow-up (Sarah's gap #2/#3): Rule 0 anchors a child chain
    // at the attachment point to its parent primary. We need a separate map
    // from the FG/atom `parentByFeature` because chains are themselves
    // primaries — they're never registered as the "child" in the FG-parent
    // logic above. Walk every chain's bonds; if any backbone atom bonds to
    // an atom in another primary feature, that other primary is the chain's
    // numbering parent.
    const chainParentByFeature = new Map<Feature, Feature>();
    for (const f of features) {
        if (classifyFeature(f) !== "chain") continue;
        const myAtoms = new Set(f.atoms);
        let parent: Feature | undefined;
        outer: for (const atom of f.atoms) {
            for (const bond of graph.getBondsAttachedTo(atom)) {
                const other = bond.getOtherAtom(atom);
                if (myAtoms.has(other)) continue;
                const otherPrimary = primaryFeatures.find(
                    p => p.feature !== f && p.atomSet.has(other),
                );
                if (otherPrimary) {
                    parent = otherPrimary.feature;
                    break outer;
                }
            }
        }
        if (parent) chainParentByFeature.set(f, parent);
    }

    // Phase 3.6 (PIPH-309): IUPAC-like atom numbering. Reorder each primary
    // feature's atoms so the principal functional group (or heteroatom /
    // unsaturation) receives the lowest locant. The label resolver numbers
    // atoms by their position in the feature's atom array, so reordering here
    // changes the numbering with no labeling-code changes. This must run before
    // Phase 4 so that (a) labels use the new order and (b) computePositionInParent
    // can index into a parent's reordered array.
    //
    // Build the per-primary substituent lists from the parent map computed above,
    // then compute the reordered atom list for each ring and chain. Bridged
    // bicyclics keep their existing PIPH-118 path ordering (handled in Phase 4).
    const substituentsByParent = new Map<Feature, SubstituentInfo[]>();
    for (const [child, parent] of parentByFeature) {
        if (!primaryTypes.has(classifyFeature(parent))) continue;
        const list = substituentsByParent.get(parent) ?? [];
        list.push({
            feature: child,
            priority: functionalGroupPriority(child.name),
            anchorAtom: computeAtomAnchorForGroup(child, parent, graph),
        });
        substituentsByParent.set(parent, list);
    }

    // PIPH-309 follow-up (Sarah's gap #1): inject priority-17 "connection to
    // another primary feature" pseudo-substituents so a primary's anchor
    // cascade can fall back on the inter-primary attachment atom when no
    // functional group is present. The priority list documents this at
    // rank 17 but the code never registered it before.
    for (const f of features) {
        if (!primaryTypes.has(classifyFeature(f))) continue;
        const list = substituentsByParent.get(f) ?? [];
        const myAtoms = new Set(f.atoms);
        const seenAnchors = new Set<GraphAtom>();
        for (const atom of f.atoms) {
            for (const bond of graph.getBondsAttachedTo(atom)) {
                const other = bond.getOtherAtom(atom);
                if (myAtoms.has(other)) continue;
                // Skip if this atom is already registered as a substituent
                // anchor (the FG path handles its own anchor).
                if (seenAnchors.has(atom)) continue;
                const otherPrimary = primaryFeatures.find(
                    p => p.feature !== f && p.atomSet.has(other),
                );
                if (!otherPrimary) continue;
                list.push({
                    feature: otherPrimary.feature,
                    priority: 17,
                    anchorAtom: atom,
                });
                seenAnchors.add(atom);
            }
        }
        if (list.length) substituentsByParent.set(f, list);
    }

    const orderedAtomsByFeature = new Map<Feature, GraphAtom[]>();
    for (const f of features) {
        const ftype = classifyFeature(f);
        const subs = substituentsByParent.get(f) ?? [];
        if (ftype === "ring") {
            orderedAtomsByFeature.set(f, reorderRingAtoms(f, graph, subs));
        } else if (ftype === "chain") {
            orderedAtomsByFeature.set(f, reorderChainAtoms(f, graph, subs, chainParentByFeature));
        } else if (ftype === "bridged_bicyclic") {
            // PIPH-315: reorder bicyclic atoms HERE (not only in Phase 4) so the
            // IUPAC path order is available when a child FG's positionInParent is
            // computed against this parent. Without it, the FG position is taken
            // against parse-order `parent.atoms` while the parent's atomIndices
            // use the reordered list — so e.g. an alcohol resolves to a
            // bridgehead's number. Same compute Phase 4 uses; guarded identically
            // so irregular bicycles fall through to the `?? f.atoms` fallback.
            const bridgeheads = computeBridgeheads(f, graph);
            if (bridgeheads.length === 2) {
                const paths = computeBicyclicPaths(f, graph, bridgeheads);
                if (paths) {
                    orderedAtomsByFeature.set(
                        f,
                        buildOrderedAtomList(f.atoms, paths.mainRingPaths, paths.bridgeAtomIndices),
                    );
                }
            }
        }
    }

    // PIPH-318: molecule-scoped index/total for unclaimed atoms (singleton
    // "atom" features). Used below so the "Other atoms" list enumerates
    // same-element atoms (Chlorine-1..5) instead of repeating bare "Chlorine".
    const moleculeElementIndices = computeMoleculeElementIndices(features, graph);

    // Phase 4: build serialized features
    const serializedFeatures: SerializedFeature[] = features.map((f, idx) => {
        const id = idByFeature.get(f)!;
        const type = classifyFeature(f);

        // PIPH-118 / PIPH-309 / PIPH-315: atom ordering (rings, chains, AND
        // bridged bicyclics) is computed in the Phase 3.6 `orderedAtomsByFeature`
        // pre-pass, so the same order drives this feature's atomIndices/labels
        // AND any child FG's positionInParent (computed below against the
        // parent's stored order). Falls back to parse order when no rule applies.
        let orderedAtoms = orderedAtomsByFeature.get(f) ?? f.atoms;
        // Bridgehead/path metadata for the serialized structural fields
        // (bridgeheadAtomIndices / mainRingPaths / bridgeAtomIndices). The
        // ordering itself already came from Phase 3.6 above.
        let bridgeheads: number[] = [];
        let paths: ReturnType<typeof computeBicyclicPaths> = undefined;

        if (type === "bridged_bicyclic") {
            bridgeheads = computeBridgeheads(f, graph);
            if (bridgeheads.length === 2) {
                paths = computeBicyclicPaths(f, graph, bridgeheads);
            }
        }

        // PIPH-79: per-feature scoped atom labels via the naming module.
        // Indexing is computed within this feature's scope so the same atom
        // can have different labels in different features (e.g. "Carbon-1" in
        // an ester vs "Carbon-3" in the chain it sits on).
        // PIPH-118: Use orderedAtoms (which may be reordered for bridged bicyclics).
        // PIPH-196: For ring-like features (ring, bridged_bicyclic) every atom
        // gets a positional suffix matching its position in the feature's atom
        // walk (1-indexed), regardless of element duplication. This overloads
        // nameAtom's elementIndex param: for rings it means "position in ring",
        // not "Nth atom of this element". A 4-ring C-O-C-O becomes
        // Carbon-1, Oxygen-2, Carbon-3, Oxygen-4.
        let atomLabels: string[];
        if (type === "ring" || type === "bridged_bicyclic") {
            atomLabels = orderedAtoms.map((a, i) =>
                nameAtom(a.symbol, a.charge, a.lonePairs, i + 1, orderedAtoms.length),
            );
        } else if (type === "atom") {
            // PIPH-318: singleton "atom" features (unclaimed atoms) number at
            // MOLECULE scope, not within this 1-atom feature — so five separate
            // chlorines read Chlorine-1..5. `nameAtom`'s `total > 1` gate keeps a
            // lone unclaimed atom bare. Atoms without a molecule-index entry
            // (e.g. multi-atom atom features, out of scope) fall back to
            // single-scope indexing.
            const atomSymbols = orderedAtoms.map(a => a.symbol);
            const indexEntries = indexElementsInScope(atomSymbols);
            atomLabels = orderedAtoms.map((a, i) => {
                const mol = moleculeElementIndices.get(a.index);
                const elementIndex = mol ? mol.elementIndex : indexEntries[i].elementIndex;
                const totalOfElement = mol ? mol.totalOfElement : indexEntries[i].totalOfElement;
                return nameAtom(a.symbol, a.charge, a.lonePairs, elementIndex, totalOfElement);
            });
        } else {
            const atomSymbols = orderedAtoms.map(a => a.symbol);
            const indexEntries = indexElementsInScope(atomSymbols);
            atomLabels = orderedAtoms.map((a, i) =>
                nameAtom(
                    a.symbol,
                    a.charge,
                    a.lonePairs,
                    indexEntries[i].elementIndex,
                    indexEntries[i].totalOfElement,
                ),
            );
        }

        const sf: SerializedFeature = {
            id,
            // PIPH-79: name comes from the canonical naming module (was: uniqueName ?? name).
            name: nameFeature(f.name, f.uniqueName),
            internalName: f.name,
            type,
            // PIPH-118: Use orderedAtoms for consistent numbering
            atomIndices: orderedAtoms.map(a => a.index),
            atomLabels,
            connectionAtomIndices: f.connections.map(a => a.index),
        };

        // Item 4: parent + position. Prefer the runtime Feature.parent (set by the
        // navigation extractor pipeline) and fall back to the parent we derived above.
        const parent = f.parent ?? parentByFeature.get(f);
        if (parent) {
            const parentId = idByFeature.get(parent);
            if (parentId) {
                sf.parentFeatureId = parentId;
                // PIPH-309: index into the parent's reordered atom list so the
                // recorded position matches the new IUPAC-like numbering.
                const parentOrdered = orderedAtomsByFeature.get(parent) ?? parent.atoms;
                sf.positionInParent = computePositionInParent(f, parentOrdered);
            }
        }

        // Item 3: connections to sibling primary features
        if (f.siblings && f.siblings.length > 0) {
            const conns = serializeSiblings(f.siblings, idByFeature);
            if (conns.length > 0) sf.connections = conns;
        }

        // Item 6: fused ring relationships (bidirectional)
        const fusionEntries = fusionMap[idx];
        if (fusionEntries && fusionEntries.length > 0) {
            sf.fusedTo = fusionEntries.map(e => idByFeature.get(features[e.otherFeatureIndex])!);
            sf.fusedSharedAtoms = fusionEntries.map(e => e.sharedAtomIndices);
        }

        // Item 5: bridgeheads for bridged bicyclic features
        // PIPH-118: We already computed bridgeheads and paths above for atom reordering,
        // so reuse those results instead of recomputing.
        if (type === "bridged_bicyclic") {
            if (bridgeheads.length > 0) {
                sf.bridgeheadAtomIndices = bridgeheads;
            }
            // PIPH-61: pre-compute main ring paths and bridge atoms from bond
            // counting. The spec explicitly says the legacy JSON bridge array
            // is unreliable — derive everything fresh here so templates have
            // trustworthy data.
            if (paths) {
                sf.mainRingPaths = paths.mainRingPaths;
                sf.bridgeAtomIndices = paths.bridgeAtomIndices;
                // PIPH-119: Compute bridge numbering from path lengths.
                // Each main ring path's "length" is intermediate atoms (length - 2
                // to exclude bridgeheads). The bridge length is its atom count.
                // Sort descending to get IUPAC-style notation (e.g., "2.2.1").
                const pathLengths = paths.mainRingPaths.map(p => p.length - 2);
                pathLengths.push(paths.bridgeAtomIndices.length);
                pathLengths.sort((a, b) => b - a);
                sf.bridgeNumbering = pathLengths.join(".");
            }
        }

        return sf;
    });

    // Phase 4.5 (PIPH-59): serialize bonds and build the per-atom bond-index map.
    // Bonds are stored once at the top level; atoms reference them by index.
    // Hydrogen-to-heavy-atom bonds are included so consumers can reason about
    // explicit hydrogens, but the per-atom hydrogenCount makes most consumers
    // not need to walk them.
    const serializedBonds: SerializedBond[] = graph.bonds.map((bond, i) => ({
        index: i,
        atomIndices: [bond.a1.index, bond.a2.index],
        order: bond.order,
        bondType: (bond.type ?? "l") as "l" | "w" | "d" | "s",
        // PIPH-269: surface E/Z so the fact block can emit "E configuration on alkene".
        ...(bond.isomerConfig && {isomerConfig: bond.isomerConfig}),
    }));

    const bondIndicesByAtom = new Map<number, number[]>();
    for (const sb of serializedBonds) {
        for (const ai of sb.atomIndices) {
            const list = bondIndicesByAtom.get(ai);
            if (list) {
                list.push(sb.index);
            } else {
                bondIndicesByAtom.set(ai, [sb.index]);
            }
        }
    }

    // Phase 5: build serialized atoms (only atoms that belong to at least one feature)
    const bridgeheadAtomIndices = new Set<number>();
    for (const sf of serializedFeatures) {
        if (sf.bridgeheadAtomIndices) {
            for (const i of sf.bridgeheadAtomIndices) bridgeheadAtomIndices.add(i);
        }
    }

    // PIPH-103: Primary feature types for shared-atom determination
    // An atom is only "shared" if it belongs to 2+ PRIMARY features (ring, chain, etc.)
    // Atoms that appear in both a primary and a functional group are NOT shared.
    const PRIMARY_FEATURE_TYPES = ["ring", "chain", "bridged_bicyclic", "atom_cluster"];

    const serializedAtoms: SerializedAtom[] = [];
    for (const [atom, featureSet] of atomToFeatures) {
        const featureIds = Array.from(featureSet).map(f => idByFeature.get(f)!);
        // PIPH-103: Only count primary features for shared determination
        const primaryFeatureCount = Array.from(featureSet).filter(f =>
            PRIMARY_FEATURE_TYPES.includes(classifyFeature(f))
        ).length;
        const atomEntry: SerializedAtom = {
            index: atom.index,
            symbol: atom.symbol,
            ...(atom.id !== undefined && { id: atom.id }),
            shared: primaryFeatureCount > 1,
            featureIds,
            // PIPH-59 fields
            charge: atom.charge,
            lonePairs: atom.lonePairs,
            ...(atom.radicals > 0 && { radicals: atom.radicals }),
            // PIPH-269: surface R/S so the fact block can emit "S configuration at carbon-2".
            ...(atom.chirality && { chirality: atom.chirality }),
            hydrogenCount: computeHydrogenCount(atom, graph),
            bondIndices: bondIndicesByAtom.get(atom.index) ?? [],
        };
        if (bridgeheadAtomIndices.has(atom.index)) {
            atomEntry.bridgehead = true;
        }
        serializedAtoms.push(atomEntry);
    }
    // Stable ordering by atom index for snapshot-friendly output
    serializedAtoms.sort((a, b) => a.index - b.index);

    return {features: serializedFeatures, atoms: serializedAtoms, bonds: serializedBonds};
}

/**
 * Total number of hydrogens bonded to an atom — both explicit H neighbors
 * (real atoms with symbol "H") and implicit H counts on the atom itself.
 * Used by the PIPH-59 atom template to format subjects like "NH" and "OH".
 */
function computeHydrogenCount(atom: GraphAtom, graph: MolecularGraph): number {
    let count = atom.implicitH;
    for (const bond of graph.getBondsAttachedTo(atom)) {
        const other = bond.getOtherAtom(atom);
        if (other.symbol === "H") count++;
    }
    return count;
}

/**
 * Carbonyl-family functional groups (ester, ketone, amide, aldehyde,
 * acid halide, carboxylic acid, anhydride, plain carbonyl). All of these
 * are extracted with `connections[0]` set to the carbonyl C — see
 * `carbonylExtractor` and `carbonylCoreExtractor`.
 */
function isCarbonylFamily(feature: Feature): boolean {
    return feature.name === "carbonyl" || feature.name.startsWith("carbonyl.");
}

/**
 * 1-indexed position of a sub-feature within its parent's atom list.
 *
 * For shared-atom functional groups (e.g. a ketone whose carbonyl C sits on
 * a chain), the child's own atoms overlap the parent's atoms and we use the
 * minimum overlap position.
 *
 * For own-atom functional groups (e.g. an alcohol whose O is its own atom
 * attached to a ring C), the child's own atoms have no overlap with the
 * parent — we fall back to the CONNECTION atoms, which point to the parent's
 * atom that the group is bonded to.
 *
 * PIPH-248: Carbonyl-family groups (ester/ketone/amide/...) have a chemically
 * meaningful anchor — the carbonyl C, which is reliably `connections[0]` per
 * the carbonyl extractors. For a lactone (cyclic ester) BOTH the carbonyl C
 * and the ester ether O sit in the parent ring, so the generic `Math.min`
 * fallback would arbitrarily pick whichever has the lower ring index. That
 * surfaces as "ester at oxygen-1" instead of the conventional
 * "ester at carbon-N". Prefer the carbonyl C anchor when it's in the parent.
 */
// PIPH-309: `parentOrdered` is the parent's reordered atom list (Phase 3.6).
// Positions are 1-indexed into THAT list so they match the new numbering. When
// the parent wasn't reordered, parentOrdered === parent.atoms and behavior is
// unchanged.
function computePositionInParent(
    child: Feature,
    parentOrdered: GraphAtom[],
): number | undefined {
    // PIPH-248: Carbonyl-family groups anchor at the carbonyl C (connections[0]).
    if (isCarbonylFamily(child) && child.connections.length > 0) {
        const anchorPos = parentOrdered.indexOf(child.connections[0]) + 1;
        if (anchorPos > 0) return anchorPos;
    }

    // First try: own-atom overlap (shared-carbon groups like aldehyde/ketone)
    const ownPositions = child.atoms
        .map(a => parentOrdered.indexOf(a) + 1)
        .filter(p => p > 0);
    if (ownPositions.length > 0) {
        return Math.min(...ownPositions);
    }
    // Fallback: connection-atom overlap (own-atom groups like alcohol/halide)
    const connPositions = child.connections
        .map(a => parentOrdered.indexOf(a) + 1)
        .filter(p => p > 0);
    if (connPositions.length > 0) {
        return Math.min(...connPositions);
    }
    return undefined;
}

function serializeSiblings(
    siblings: FeatureConnection[],
    idByFeature: Map<Feature, string>,
): SerializedFeatureConnection[] {
    const out: SerializedFeatureConnection[] = [];
    for (const sib of siblings) {
        const toId = idByFeature.get(sib.feature);
        if (!toId) continue;
        out.push({
            toFeatureId: toId,
            atomIndices: sib.connections.map(a => a.index),
        });
    }
    return out;
}

/**
 * Bridgehead atoms in a bridged bicyclic system are those with ≥3 internal bonds
 * (bonds to other atoms in the same merged ring set). For norbornane this gives
 * the two ring-junction carbons.
 *
 * @public Exported for PIPH-118 (HTML tree atom ordering)
 */
export function computeBridgeheads(feature: Feature, graph: MolecularGraph): number[] {
    const featureAtomSet = new Set(feature.atoms);
    const bridgeheads: number[] = [];
    for (const atom of feature.atoms) {
        const internalBondCount = graph
            .getBondsAttachedTo(atom)
            .filter(b => featureAtomSet.has(b.getOtherAtom(atom)))
            .length;
        if (internalBondCount >= 3) {
            bridgeheads.push(atom.index);
        }
    }
    return bridgeheads;
}

/**
 * Compute the two main ring paths and the bridge for a bridged bicyclic
 * feature (PIPH-61). Uses simple-path enumeration over the feature's internal
 * bond graph, starting at one bridgehead and ending at the other.
 *
 * Algorithm:
 *   1. Build an adjacency map restricted to the feature's own atoms.
 *   2. DFS all simple paths between the two bridgeheads.
 *   3. Sort paths by length (longest first).
 *   4. The two longest paths are the "main ring paths"; the shortest is the
 *      bridge. Bridge atoms are the intermediates (path minus bridgeheads).
 *
 * Returns undefined if the feature doesn't produce the expected ≥3 simple paths
 * (i.e. not a true bridged bicyclic — possibly a fused-ring merge or other
 * irregular system). The caller falls back to leaving the fields unset.
 *
 * @public Exported for PIPH-118 (HTML tree atom ordering)
 */
export function computeBicyclicPaths(
    feature: Feature,
    graph: MolecularGraph,
    bridgeheads: number[],
): {mainRingPaths: number[][]; bridgeAtomIndices: number[]} | undefined {
    if (bridgeheads.length !== 2) return undefined;

    // Build adjacency map restricted to the feature's atoms.
    const featureAtomSet = new Set(feature.atoms);
    const adjByIndex = new Map<number, number[]>();
    for (const atom of feature.atoms) {
        const neighbors: number[] = [];
        for (const bond of graph.getBondsAttachedTo(atom)) {
            const other = bond.getOtherAtom(atom);
            if (featureAtomSet.has(other)) {
                neighbors.push(other.index);
            }
        }
        adjByIndex.set(atom.index, neighbors);
    }

    const [start, end] = bridgeheads;

    // Enumerate all simple paths from start to end. For typical bicyclics
    // (2..4 ring atoms on each side) the path count is tiny so this is cheap.
    // Cap the search depth at the feature's atom count to prevent runaway.
    const maxDepth = feature.atoms.length;
    const allPaths: number[][] = [];
    const visited = new Set<number>([start]);
    const current: number[] = [start];

    function dfs(node: number) {
        if (node === end && current.length > 1) {
            allPaths.push(current.slice());
            return;
        }
        if (current.length > maxDepth) return;
        const neighbors = adjByIndex.get(node) ?? [];
        for (const next of neighbors) {
            if (visited.has(next)) continue;
            visited.add(next);
            current.push(next);
            dfs(next);
            current.pop();
            visited.delete(next);
        }
    }
    dfs(start);

    // A valid bridged bicyclic has at least 3 simple paths between bridgeheads.
    if (allPaths.length < 3) return undefined;

    // Sort by length (longest first). Stable sort preserves deterministic order
    // for ties.
    allPaths.sort((a, b) => b.length - a.length);

    // The two longest paths are the main ring paths; the shortest is the bridge.
    const mainRingPaths = [allPaths[0], allPaths[1]];
    const bridgePath = allPaths[allPaths.length - 1];
    const bridgeAtomIndices = bridgePath.slice(1, -1); // strip bridgeheads

    return {mainRingPaths, bridgeAtomIndices};
}

/**
 * PIPH-118: Reorder atoms for a bridged bicyclic feature to follow structural
 * path order. This ensures consistent numbering between atom descriptions and
 * feature descriptions.
 *
 * Ordering: bridgehead1 → path1 intermediates → bridgehead2 → path2 intermediates → bridge atoms
 *
 * @param atoms The original atom list from the feature
 * @param mainRingPaths The two main ring paths (each includes bridgeheads)
 * @param bridgeAtomIndices The bridge atoms (excluding bridgeheads)
 * @returns Reordered atom list
 *
 * @public Exported for PIPH-118 (HTML tree atom ordering)
 */
export function buildOrderedAtomList(
    atoms: GraphAtom[],
    mainRingPaths: number[][],
    bridgeAtomIndices: number[],
): GraphAtom[] {
    // Build a map from atom index to atom object for lookup
    const atomByIndex = new Map<number, GraphAtom>();
    for (const atom of atoms) {
        atomByIndex.set(atom.index, atom);
    }

    const ordered: GraphAtom[] = [];
    const seen = new Set<number>();

    // Add first path (includes both bridgeheads and intermediates)
    for (const idx of mainRingPaths[0]) {
        if (!seen.has(idx)) {
            const atom = atomByIndex.get(idx);
            if (atom) {
                ordered.push(atom);
                seen.add(idx);
            }
        }
    }

    // Add second path intermediates (bridgeheads already added from first path)
    for (const idx of mainRingPaths[1]) {
        if (!seen.has(idx)) {
            const atom = atomByIndex.get(idx);
            if (atom) {
                ordered.push(atom);
                seen.add(idx);
            }
        }
    }

    // Add bridge atoms
    for (const idx of bridgeAtomIndices) {
        if (!seen.has(idx)) {
            const atom = atomByIndex.get(idx);
            if (atom) {
                ordered.push(atom);
                seen.add(idx);
            }
        }
    }

    // Add any remaining atoms not in paths (shouldn't happen for valid bicyclics,
    // but defensive)
    for (const atom of atoms) {
        if (!seen.has(atom.index)) {
            ordered.push(atom);
        }
    }

    return ordered;
}

// ============================================================================
// PIPH-309: IUPAC-like atom numbering via atom-index reordering.
//
// The label resolver numbers atoms purely by their position in a feature's
// atom array, so reordering that array changes the numbering with no changes
// to any labeling code. These helpers reorder ring and chain atoms so the
// principal functional group (or heteroatom / unsaturation) receives the
// lowest locant, approximating IUPAC numbering. Every helper falls back to the
// feature's original parse order on any ambiguity to preserve determinism.
// ============================================================================

/** A functional group (or connection) attached to a primary, with its locant anchor. */
interface SubstituentInfo {
    feature: Feature;
    /** Priority rank from functionalGroupPriority (1 = highest priority). */
    priority: number;
    /** The parent atom that owns this group's locant, if resolvable. */
    anchorAtom?: GraphAtom;
}

/**
 * Heteroatom symbols that anchor ring numbering (Pattern D) when no principal
 * functional group is present.
 */
const RING_HETEROATOMS = new Set(["N", "O", "S"]);

/**
 * PIPH-309 follow-up (Sarah's spec amendment): when a ring contains multiple
 * heteroatoms, the highest-priority element wins position 1. Ranking is
 * N > O > S (matching IUPAC heterocyclic convention). Ties broken by lowest
 * graph index for determinism.
 */
const HETEROATOM_RANK: Record<string, number> = {N: 0, O: 1, S: 2};

function pickHighestPriorityHeteroatom(heteroatoms: Iterable<GraphAtom>): GraphAtom {
    let best: GraphAtom | undefined;
    for (const a of heteroatoms) {
        if (!best) {
            best = a;
            continue;
        }
        const bestRank = HETEROATOM_RANK[best.symbol] ?? 99;
        const aRank = HETEROATOM_RANK[a.symbol] ?? 99;
        if (aRank < bestRank) best = a;
        else if (aRank === bestRank && a.index < best.index) best = a;
    }
    return best!;
}

/**
 * PIPH-309: Map a functional group internalName to an IUPAC-like priority rank
 * (1 = highest priority, gets the lowest locant). Mirrors the principal
 * characteristic group hierarchy from the spec. Checked as an ordered cascade
 * because of dot-notation families (e.g. "carbonyl.amide.2" must match the
 * amide rule before the generic carbonyl rule).
 *
 * Unknown names return a large rank so they never win an anchor.
 */
export function functionalGroupPriority(internalName: string): number {
    const n = internalName;
    if (n.startsWith("carbonyl.carboxylicAcid")) return 1;
    if (n.startsWith("carbonyl.acidHalide")) return 2;
    if (n.startsWith("carbonyl.acidAnhydride")) return 3;
    if (n.startsWith("carbonyl.ester")) return 4;
    if (n.startsWith("carbonyl.amide")) return 5;
    if (n === "nitrile") return 6;
    if (n.startsWith("carbonyl.aldehyde")) return 7;
    if (n.startsWith("carbonyl.ketone") || n === "carbonyl") return 8;
    if (n === "alcohol") return 9;
    if (n === "thiol") return 10;
    if (n.startsWith("amine")) return 11;
    if (n === "alkene" || n === "alkyne") return 12;
    if (n === "ether" || n === "sulfide") return 13;
    if (n.startsWith("alkylHalide")) return 14;
    if (n === "nitro") return 15;
    if (n === "chain.1.methyl" || n === "isopropyl") return 16;
    // 17 = connection to another primary (no functional group) — handled by the
    // caller, not by internalName. 18 = unsubstituted.
    return 18;
}

/** Carbonyl-family check, mirrors isCarbonylFamily above (kept local for clarity). */
function isCarbonylFamilyName(name: string): boolean {
    return name === "carbonyl" || name.startsWith("carbonyl.");
}

/**
 * PIPH-309: Determine which parent atom "owns" a functional group's locant.
 * This is the bridge between a functional group and its parent ring/chain
 * numbering. Reproduces the precedence used by computePositionInParent so the
 * anchor and the recorded positionInParent stay consistent.
 */
export function computeAtomAnchorForGroup(
    child: Feature,
    parent: Feature,
    graph: MolecularGraph,
): GraphAtom | undefined {
    const parentSet = new Set(parent.atoms);

    // Carbonyl family: anchor at the carbonyl C, which is reliably connections[0]
    // for chain-attached groups, or an overlapping ring carbon for cyclic ones.
    if (isCarbonylFamilyName(child.name)) {
        const conn0 = child.connections[0];
        if (conn0 && parentSet.has(conn0)) return conn0;
        // Cyclic ketone / lactone: the carbonyl C is itself a ring atom.
        const overlapCarbon = child.atoms.find(a => parentSet.has(a) && a.symbol === "C");
        if (overlapCarbon) return overlapCarbon;
        // Last resort: the parent atom bonded to the carbonyl C.
        if (conn0) {
            for (const bond of graph.getBondsAttachedTo(conn0)) {
                const other = bond.getOtherAtom(conn0);
                if (parentSet.has(other)) return other;
            }
        }
    }

    // In-ring heteroatom groups (ether/sulfide whose O/S IS a ring atom):
    // anchor at the overlapping heteroatom.
    const overlapHetero = child.atoms.find(a => parentSet.has(a) && RING_HETEROATOMS.has(a.symbol));
    if (overlapHetero) return overlapHetero;

    // Own-atom groups (alcohol/thiol/halide/amine/ether/sulfide with the
    // heteroatom outside the ring): anchor at the connection atom in the parent
    // (the carbon bearing the group). Deterministic: lowest parent index.
    const connInParent = child.connections.filter(a => parentSet.has(a));
    if (connInParent.length > 0) {
        return connInParent.reduce((best, a) =>
            parent.atoms.indexOf(a) < parent.atoms.indexOf(best) ? a : best,
        );
    }

    // Fallback: any own atom in the parent.
    const ownInParent = child.atoms.filter(a => parentSet.has(a));
    if (ownInParent.length > 0) {
        return ownInParent.reduce((best, a) =>
            parent.atoms.indexOf(a) < parent.atoms.indexOf(best) ? a : best,
        );
    }

    return undefined;
}

/**
 * PIPH-309: Build adjacency restricted to a feature's own atoms.
 * Mirrors the pattern in computeBicyclicPaths but keyed by atom object.
 */
function buildFeatureAdjacency(
    atoms: GraphAtom[],
    graph: MolecularGraph,
): Map<GraphAtom, GraphAtom[]> {
    const atomSet = new Set(atoms);
    const adj = new Map<GraphAtom, GraphAtom[]>();
    for (const atom of atoms) {
        const neighbors: GraphAtom[] = [];
        for (const bond of graph.getBondsAttachedTo(atom)) {
            const other = bond.getOtherAtom(atom);
            if (atomSet.has(other)) neighbors.push(other);
        }
        adj.set(atom, neighbors);
    }
    return adj;
}

/**
 * PIPH-309: Order ring atoms into a single closed cyclic walk (each consecutive
 * pair bonded, last bonds back to first). Returns undefined if the atoms don't
 * form a simple cycle (some atom has internal degree ≠ 2) so the caller can
 * fall back to parse order.
 */
function walkRingCycle(atoms: GraphAtom[], graph: MolecularGraph): GraphAtom[] | undefined {
    if (atoms.length < 3) return undefined;
    const adj = buildFeatureAdjacency(atoms, graph);
    for (const a of atoms) {
        if ((adj.get(a) ?? []).length !== 2) return undefined;
    }
    const start = atoms[0];
    const ordered: GraphAtom[] = [start];
    const seen = new Set<GraphAtom>([start]);
    let current = start;
    while (ordered.length < atoms.length) {
        const next = (adj.get(current) ?? []).find(n => !seen.has(n));
        if (!next) break;
        ordered.push(next);
        seen.add(next);
        current = next;
    }
    if (ordered.length !== atoms.length) return undefined;
    // Confirm the cycle closes (last bonds back to first).
    if (!(adj.get(ordered[ordered.length - 1]) ?? []).includes(start)) return undefined;
    return ordered;
}

/**
 * PIPH-309: Order chain atoms end-to-end via the two degree-1 endpoints.
 * Returns undefined if the atoms don't form a simple path (e.g. branched).
 */
function walkChainBackbone(atoms: GraphAtom[], graph: MolecularGraph): GraphAtom[] | undefined {
    if (atoms.length === 0) return undefined;
    if (atoms.length === 1) return [atoms[0]];
    const adj = buildFeatureAdjacency(atoms, graph);
    const endpoints = atoms.filter(a => (adj.get(a) ?? []).length === 1);
    if (endpoints.length !== 2) return undefined;
    // Any interior atom must have exactly 2 internal neighbors (no branches).
    for (const a of atoms) {
        const deg = (adj.get(a) ?? []).length;
        if (deg !== 1 && deg !== 2) return undefined;
    }
    const start = endpoints[0];
    const ordered: GraphAtom[] = [start];
    const seen = new Set<GraphAtom>([start]);
    let current = start;
    while (ordered.length < atoms.length) {
        const next = (adj.get(current) ?? []).find(n => !seen.has(n));
        if (!next) break;
        ordered.push(next);
        seen.add(next);
        current = next;
    }
    if (ordered.length !== atoms.length) return undefined;
    return ordered;
}

/**
 * PIPH-309: 1-indexed locant set for the atoms in `anchors` given an ordering.
 * Sorted ascending for first-point-of-difference comparison.
 */
function locantSet(order: GraphAtom[], anchors: Set<GraphAtom>): number[] {
    const locants: number[] = [];
    for (let i = 0; i < order.length; i++) {
        if (anchors.has(order[i])) locants.push(i + 1);
    }
    return locants.sort((a, b) => a - b);
}

/**
 * PIPH-309: Compare two orderings by their substituent locant sets at the first
 * point of difference. Returns <0 if A is preferred (lower locants), >0 if B.
 */
function lowestLocantCompare(
    orderA: GraphAtom[],
    orderB: GraphAtom[],
    anchors: Set<GraphAtom>,
): number {
    const la = locantSet(orderA, anchors);
    const lb = locantSet(orderB, anchors);
    const len = Math.min(la.length, lb.length);
    for (let i = 0; i < len; i++) {
        if (la[i] !== lb[i]) return la[i] - lb[i];
    }
    return la.length - lb.length;
}

/**
 * PIPH-309: Pick the highest-priority substituent (lowest rank). Ties broken
 * alphabetically by feature name, then by anchor atom index for determinism.
 */
function pickPrincipalSubstituent(subs: SubstituentInfo[]): SubstituentInfo | undefined {
    const withAnchor = subs.filter(s => s.anchorAtom !== undefined);
    if (withAnchor.length === 0) return undefined;
    return withAnchor.reduce((best, s) => {
        if (s.priority !== best.priority) return s.priority < best.priority ? s : best;
        const cmp = s.feature.name.localeCompare(best.feature.name);
        if (cmp !== 0) return cmp < 0 ? s : best;
        return s.anchorAtom!.index < best.anchorAtom!.index ? s : best;
    });
}

/**
 * PIPH-309: Rotate a cyclic order so `anchor` sits at index 0, then return the
 * two candidate directions (forward and reverse, both keeping index 0 fixed).
 */
function ringCandidates(cycle: GraphAtom[], anchor: GraphAtom): [GraphAtom[], GraphAtom[]] {
    const start = cycle.indexOf(anchor);
    const forward = [...cycle.slice(start), ...cycle.slice(0, start)];
    const reverse = [forward[0], ...forward.slice(1).reverse()];
    return [forward, reverse];
}

/**
 * PIPH-309: Reorder a ring's atoms so the principal functional group (or, absent
 * one, an in-ring heteroatom, or ring unsaturation) gets the lowest locant, with
 * direction chosen for the lowest locant set. Falls back to parse order on
 * ambiguity.
 */
export function reorderRingAtoms(
    f: Feature,
    graph: MolecularGraph,
    subs: SubstituentInfo[],
): GraphAtom[] {
    const cycle = walkRingCycle(f.atoms, graph);
    if (!cycle) return f.atoms;

    const heteroatoms = new Set(f.atoms.filter(a => RING_HETEROATOMS.has(a.symbol)));

    // PIPH-309 amendment (Sarah's gap #4): heteroatom-in-ring anchors
    // position 1 — matches IUPAC heterocyclic convention (piperidin-3-ol is
    // nitrogen-1 + alcohol-3, not nitrogen-3 + alcohol-1). When both a
    // heteroatom and a functional group are present, the heteroatom still
    // wins anchor; direction minimizes the FG locant.
    //
    // Exception: when the heteroatom is itself part of the principal
    // functional group (e.g. the ring O of a lactone is part of the ester),
    // the FG's own anchor wins — the heteroatom isn't an independent ring
    // feature, it's an atom of the FG that ring-naming should reflect.
    const principal = pickPrincipalSubstituent(subs);
    const principalAtoms = principal ? new Set(principal.feature.atoms) : undefined;
    const independentHeteroatoms = principalAtoms
        ? new Set([...heteroatoms].filter(h => !principalAtoms.has(h)))
        : heteroatoms;

    let anchor: GraphAtom | undefined;
    let anchorIsHeteroatom = false;
    if (independentHeteroatoms.size > 0) {
        // Rule 2 (per amendment): heteroatom not absorbed by the principal FG
        // anchors position 1.
        anchor = pickHighestPriorityHeteroatom(independentHeteroatoms);
        anchorIsHeteroatom = true;
    } else if (principal && principal.anchorAtom) {
        // Rule 1: principal functional group anchor (no independent
        // heteroatom — either no heteroatom, or the heteroatom belongs to the
        // FG itself, as in lactone/lactam rings).
        anchor = principal.anchorAtom;
    } else {
        // Rule 3: ring unsaturation — a double-bond carbon becomes position 1,
        // and the partner becomes position 2 via direction.
        anchor = findRingDoubleBondAnchor(cycle, graph);
    }

    if (!anchor) return f.atoms; // no basis → fallback (symmetry / unsubstituted).

    const [forward, reverse] = ringCandidates(cycle, anchor);

    // Direction selection.
    // When a heteroatom anchors position 1, choose the direction that gives
    // the highest-priority FG (or remaining heteroatoms) the lowest locant.
    if (anchorIsHeteroatom) {
        // Build the priority-ordered anchors list for direction tiebreak:
        // remaining heteroatoms first (so they keep low locants), then FG
        // anchors via the standard lowest-locant-set compare.
        const directionAnchors = new Set<GraphAtom>();
        for (const h of independentHeteroatoms) {
            if (h !== anchor) directionAnchors.add(h);
        }
        for (const s of subs) if (s.anchorAtom) directionAnchors.add(s.anchorAtom);
        if (directionAnchors.size > 0) {
            const cmp = lowestLocantCompare(forward, reverse, directionAnchors);
            if (cmp !== 0) return cmp < 0 ? forward : reverse;
        }
        return forward;
    }

    // Rule 4: lowest locant set across all substituent anchors + heteroatoms.
    const anchors = new Set<GraphAtom>(heteroatoms);
    for (const s of subs) if (s.anchorAtom) anchors.add(s.anchorAtom);
    const cmp = lowestLocantCompare(forward, reverse, anchors);
    if (cmp !== 0) return cmp < 0 ? forward : reverse;

    // Rule 5 / final fallback: forward is deterministic.
    return forward;
}

/**
 * PIPH-309: Find a ring atom on a double bond to anchor an unsaturated ring.
 * Returns the lower-indexed atom of the first ring double bond, oriented so its
 * partner follows it. Returns undefined if no internal double bond exists.
 */
function findRingDoubleBondAnchor(cycle: GraphAtom[], graph: MolecularGraph): GraphAtom | undefined {
    const cycleSet = new Set(cycle);
    for (const atom of cycle) {
        for (const bond of graph.getBondsAttachedTo(atom)) {
            const other = bond.getOtherAtom(atom);
            if (bond.order === 2 && cycleSet.has(other)) {
                return atom.index <= other.index ? atom : other;
            }
        }
    }
    return undefined;
}

/**
 * PIPH-309: Reorder a chain's atoms. Child chains use the attachment point as
 * carbon-1 (Rule 0). Standalone chains number from the end closest to the
 * principal functional group, then unsaturation, then lowest locant set. Falls
 * back to parse order on ambiguity.
 */
export function reorderChainAtoms(
    f: Feature,
    graph: MolecularGraph,
    subs: SubstituentInfo[],
    parentByFeature: Map<Feature, Feature>,
): GraphAtom[] {
    const backbone = walkChainBackbone(f.atoms, graph);
    if (!backbone) return f.atoms;
    const reversed = [...backbone].reverse();

    // Rule 0: child chain — number from the end closest to the attachment
    // point so that point gets the lowest possible locant. This is
    // UNCONDITIONAL for child chains per Sarah's spec clarification — a
    // functional group on the chain does NOT override the attachment locant.
    // For mid-chain attachments, the closer end is chosen.
    const parent = parentByFeature.get(f);
    if (parent) {
        const attachIdx = chainAttachmentBackboneIndex(backbone, parent, graph);
        if (attachIdx !== undefined) {
            const forwardLocant = attachIdx + 1;
            const reverseLocant = backbone.length - attachIdx;
            return forwardLocant <= reverseLocant ? backbone : reversed;
        }
    }

    // Rule 1: principal functional group — number from the end closest to it.
    const principal = pickPrincipalSubstituent(subs);
    if (principal && principal.anchorAtom) {
        const fwdPos = backbone.indexOf(principal.anchorAtom);
        const revPos = reversed.indexOf(principal.anchorAtom);
        if (fwdPos !== revPos) return fwdPos < revPos ? backbone : reversed;
    }

    // Rule 2: unsaturation — number from the end closest to a multiple bond.
    const unsatAnchor = findChainUnsaturationAnchor(backbone, graph);
    if (unsatAnchor) {
        const fwdPos = backbone.indexOf(unsatAnchor);
        const revPos = reversed.indexOf(unsatAnchor);
        if (fwdPos !== revPos) return fwdPos < revPos ? backbone : reversed;
    }

    // Rule 3: lowest locant set across substituent anchors.
    const anchors = new Set<GraphAtom>();
    for (const s of subs) if (s.anchorAtom) anchors.add(s.anchorAtom);
    if (anchors.size > 0) {
        const cmp = lowestLocantCompare(backbone, reversed, anchors);
        if (cmp !== 0) return cmp < 0 ? backbone : reversed;
    }

    return f.atoms;
}

/**
 * PIPH-309 follow-up: For a child chain, find the backbone index of an atom
 * that bonds to the parent feature. When multiple backbone atoms attach
 * (e.g. a chain that loops back), the lowest-index attachment is returned —
 * the caller then chooses the direction (forward/reverse) that gives that
 * atom the lowest 1-indexed locant.
 *
 * Returns undefined when no backbone atom bonds to the parent (which
 * shouldn't happen for a child chain, but the caller handles it gracefully).
 */
function chainAttachmentBackboneIndex(
    backbone: GraphAtom[],
    parent: Feature,
    graph: MolecularGraph,
): number | undefined {
    const parentSet = new Set(parent.atoms);
    for (let i = 0; i < backbone.length; i++) {
        const hasBondToParent = graph
            .getBondsAttachedTo(backbone[i])
            .some(b => parentSet.has(b.getOtherAtom(backbone[i])));
        if (hasBondToParent) return i;
    }
    return undefined;
}

/**
 * PIPH-309: Find the chain backbone atom on a multiple bond, for unsaturation
 * numbering. Returns the backbone atom (whichever end's side it favors is
 * decided by the caller via position comparison).
 */
function findChainUnsaturationAnchor(backbone: GraphAtom[], graph: MolecularGraph): GraphAtom | undefined {
    const set = new Set(backbone);
    for (const atom of backbone) {
        for (const bond of graph.getBondsAttachedTo(atom)) {
            if (bond.order >= 2 && set.has(bond.getOtherAtom(atom))) {
                return atom;
            }
        }
    }
    return undefined;
}
